/**
 * SectionGuard - enforces the admin "Site Sections" toggles on direct URLs.
 *
 * - Section ON  -> renders the page.
 * - Section OFF -> learners/visitors see a friendly "not available" page (not an error);
 *                  admins still see the page with a banner, so content can be managed/previewed.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { EyeOff, ArrowLeft } from 'lucide-react';
import { useFeatureFlags, ADMIN_ROLES } from '../contexts/FeatureFlagsContext';
import { useAuth } from '../contexts/AuthContext';
import { Header } from './layout/Header';

export const SECTION_LABELS = {
  courses: 'Courses',
  external_courses: 'External Courses',
  learning_labs: 'Learning Labs',
  learning_pathways: 'Learning Pathways',
  masterclasses: 'Masterclasses',
  programmes: 'Programmes',
  campaigns: 'Campaigns',
  ebooks: 'eBooks',
  wsp: 'Workplace Skills Plan',
  certificates: 'Certificates',
  wishlists: 'Wishlists',
  leaderboard: 'Leaderboard',
  cpd_tracking: 'CPD Tracking',
  regulatory_alerts: 'Regulatory Alerts',
  navigate_academy: 'Navigate Academy',
};

export function SectionUnavailable({ section }) {
  const label = SECTION_LABELS[section] || 'This section';
  return (
    <div className="min-h-screen bg-navigate-dark flex flex-col" data-testid="section-unavailable">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-navigate-surface border border-zinc-800 flex items-center justify-center">
            <EyeOff className="h-7 w-7 text-navigate-gold" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white mb-2">{label} is not available</h1>
          <p className="text-zinc-400 mb-8">
            This section is currently turned off for your organisation. Please contact your administrator if you think this is a mistake.
          </p>
          <Link to="/dashboard" className="btn-gold inline-flex items-center gap-2" data-testid="section-unavailable-home">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function SectionGuard({ section, children }) {
  const { isFeatureEnabled, loading } = useFeatureFlags();
  const { user } = useAuth();
  if (!section || isFeatureEnabled(section)) return children;
  if (loading) return children; // avoid flashing "not available" before flags load
  const isAdmin = ADMIN_ROLES.includes((user?.role || '').toLowerCase());
  if (isAdmin) {
    return (
      <>
        <div className="sticky top-0 z-[60] bg-amber-500/90 text-black text-sm text-center py-1.5 px-4" data-testid="section-hidden-admin-banner">
          <EyeOff className="h-4 w-4 inline mr-2" />
          {SECTION_LABELS[section] || 'This section'} is turned off, so learners cannot see this page. You can see it because you are an admin.
        </div>
        {children}
      </>
    );
  }
  return <SectionUnavailable section={section} />;
}
